import React, { useState } from 'react';
import type { ISolutionInfoProps } from './solution-info';
import { dtoToInfoProps, SolutionInfo } from './solution-info';
import s from './solution-page.scss';
import type { IOverViewProps } from './solution-overview/solution-overview';
import {
  dtoToOverViewProps,
  SolutionOverView,
} from './solution-overview/solution-overview';
import { SolutionRateReviewsContainer } from './solution-rate-and-reviews/solution-rate-reviews-container';
import type { WithTranslation as WithTranslationProps } from 'react-i18next';
import type { ISolutionsGridContainerProps } from './solutions-grid/solutions-grid-container';
import {
  dtoToGridsSolutions,
  SolutionsGridContainer,
} from './solutions-grid/solutions-grid-container';
import type { ISolutionPlansProps } from './solution-plans';
import { dtoToSolutionPlansProps, SolutionPlans } from './solution-plans';
import type { IRoute } from '../../..';
import { AppPageMode } from '../../..';
import {
  PlansPreivewWrapper,
  ReviewsPreivewWrapper,
  SuggestionPreviewWrapper,
} from '../preview-elements';
import type { ISolutionBreadcrumbsSectionProps } from '../../breadcrumbs-section/solution-breadcrumbs-section';
import {
  dtoToBreadcrumbsSectionProps,
  SolutionBreadcrumbsSection,
} from '../../breadcrumbs-section/solution-breadcrumbs-section';
import { Box, Cell, Divider } from '@wix/design-system';
import { FluidContainer } from '../../FluidContainer';
import { useConfigContext } from '../../../config-context';
import { composer } from '../../../component-decorators/composer';
import { VisibilityDetector } from '@wix/app-market-components';
import { sendAppPageImpressionsBi } from '../../../common/bi-events';
import { BackButton } from '../../back-button';
import type {
  RequiredApp,
  WebSolution,
} from '@wix/ambassador-marketplace/types';
import type { InjectedExperimentsProps } from '@wix/wix-experiments-react';
import { config } from '../../../config';
import type { MarketplaceStore } from '../../../component-decorators/with-marketplace-store';
import { Origin } from '../../../enums/marketplace-enums';
import type { ManagedApp } from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import {
  AppGroup,
  IncompleteSetupReason,
} from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import { SolutionHelper } from './solution-helper/solution-helper';

export interface ISolutionPageProps
  extends Partial<WithTranslationProps>,
    Partial<InjectedExperimentsProps> {
  marketplaceStore?: MarketplaceStore;
  appName: string;
  solutionId: string;
  developerName: string;
  solutionBreadcrumbsSectionProps: ISolutionBreadcrumbsSectionProps;
  solutionInfoProps: ISolutionInfoProps;
  solutionOverViewProps: IOverViewProps;
  solutionPlansProps: ISolutionPlansProps;
  solutionDeveloperIcon: string;
  solutionsGridProps?: ISolutionsGridContainerProps;
  mode?: AppPageMode;
  appDependencies: any;
  isNotAvailableToInstall?: boolean;
  preventNewOrdersInstallation?: boolean;
}

export interface IAppDependenciesProps {
  needToInstallApps: RequiredApp[];
  showAppDependencyHelper: 'None' | 'App-Req-Installation' | 'App-Req-Pending';
}

export const dtoToSolutionPageProps = (
  solutionDto: WebSolution & {
    categories: {
      slug: string;
      name: string;
    }[];
  },
  prevRoute: IRoute,
  mode?: AppPageMode,
  installedApps?: ManagedApp[],
  origin?: Origin,
  isNotAvailableToInstall?: boolean,
  preventNewOrdersInstallation?: boolean,
): ISolutionPageProps => {
  return {
    appName: solutionDto.baseInfo.name,
    solutionId: solutionDto.baseInfo.id,
    solutionDeveloperIcon: solutionDto.baseInfo.icon,
    developerName: solutionDto.baseInfo.company
      ? solutionDto.baseInfo.company.name
      : 'Dev Name',
    appDependencies: dtoToAppDependenciesProps(
      solutionDto,
      installedApps,
      origin,
    ),
    solutionOverViewProps: dtoToOverViewProps(solutionDto),
    solutionInfoProps: dtoToInfoProps(
      solutionDto,
      isNotAvailableToInstall,
      preventNewOrdersInstallation,
    ),
    solutionPlansProps: dtoToSolutionPlansProps(solutionDto, mode),
    solutionsGridProps: dtoToGridsSolutions(solutionDto),
    solutionBreadcrumbsSectionProps: dtoToBreadcrumbsSectionProps(
      solutionDto,
      prevRoute,
    ),
  };
};
export const dtoToAppDependenciesProps = (
  solutionDto: WebSolution,
  installedApps: ManagedApp[],
  origin: Origin,
): IAppDependenciesProps => {
  if (origin === Origin.STANDALONE || installedApps == undefined) {
    return {
      needToInstallApps: solutionDto.requiredApps,
      showAppDependencyHelper: 'None',
    };
  }

  if (
    installedApps?.some(
      (installedApp) =>
        installedApp.appId === solutionDto.baseInfo.id &&
        installedApp.installationDetails.incompleteSetupReason !==
          IncompleteSetupReason.PENDING_REQUIRED_INSTALLATIONS,
    )
  ) {
    return { needToInstallApps: [], showAppDependencyHelper: 'None' };
  }

  if (!solutionDto?.requiredApps?.length) {
    return { needToInstallApps: [], showAppDependencyHelper: 'None' };
  }

  const installedAppRequirements = solutionDto?.requiredApps?.some(
    (requiredApp) =>
      installedApps?.some(
        (installedApp) =>
          installedApp.appId === requiredApp.id &&
          (installedApp.installationDetails.group === AppGroup.INSTALLED ||
            installedApp.installationDetails.group ===
              AppGroup.BUSINESS_SOLUTIONS),
      ),
  );
  if (installedAppRequirements) {
    return { needToInstallApps: [], showAppDependencyHelper: 'None' };
  }
  const pendingApps = installedApps?.filter(
    (installedApp) =>
      installedApp.installationDetails.group === AppGroup.PENDING &&
      installedApp.installationDetails.incompleteSetupReason !==
        IncompleteSetupReason.PENDING_REQUIRED_INSTALLATIONS,
  );
  if (pendingApps?.length) {
    const pendingAppRequirements = solutionDto?.requiredApps?.find(
      (requiredApp) =>
        pendingApps?.some((pendingApp) => pendingApp.appId === requiredApp.id),
    );
    if (pendingAppRequirements && origin === Origin.BIZMGR) {
      return {
        needToInstallApps: [pendingAppRequirements],
        showAppDependencyHelper: 'App-Req-Pending',
      };
    }
    if (pendingAppRequirements && origin === Origin.EDITOR) {
      return { needToInstallApps: [], showAppDependencyHelper: 'None' };
    }
  }
  return {
    needToInstallApps: solutionDto?.requiredApps,
    showAppDependencyHelper: 'App-Req-Installation',
  };
};

export const SolutionPage = composer()
  .withMarketplaceStore()
  .withExperiments()
  .compose(
    ({
      solutionId,
      developerName,
      solutionDeveloperIcon,
      solutionInfoProps,
      appDependencies,
      solutionOverViewProps,
      solutionPlansProps,
      solutionsGridProps,
      solutionBreadcrumbsSectionProps,
      mode,
      marketplaceStore,
      experiments,
    }: ISolutionPageProps) => {
      const { locale, countryCode, isMobile, getAppInstance } =
        useConfigContext();
      const [sectionsWithBi, setSectionsWithBi] = useState([]);

      const shouldSwitchOrderPricingAndReviews = experiments.enabled(
        'specs.marketplace.switchOrderReviewsPricing',
      );

      const onSectionImpression = (
        sectionName: string,
        sectionIndex: number,
      ) => {
        if (!sectionsWithBi.includes(sectionName)) {
          setSectionsWithBi((sectionsWithBi) => [
            ...sectionsWithBi,
            sectionName,
          ]);
          sendAppPageImpressionsBi(sectionName, sectionIndex, solutionId);
        }
      };
      const showBackToReferrerButton =
        config.origin === Origin.BIZMGR &&
        config.goBackToReferrer &&
        marketplaceStore.route.currentUrl ===
          marketplaceStore.prevRoute.currentUrl;
      const showRegularBackButton =
        marketplaceStore.prevRoute &&
        marketplaceStore.prevRoute.currentUrl !==
          marketplaceStore.route.currentUrl &&
        marketplaceStore.route.referral !== 'menu_navigation' &&
        marketplaceStore.route.referral !== 'back_button';

      const showBackButton = showBackToReferrerButton || showRegularBackButton;

      return (
        <FluidContainer
          vocab="https://schema.org/"
          typeof="SoftwareApplication"
          className={s.fluidContainerFlex}
        >
          <Cell>
            <Box
              className={s.breadcrumbs}
              direction={isMobile ? 'vertical' : 'horizontal'}
              verticalAlign="middle"
            >
              {showBackButton && (
                <BackButton
                  appId={solutionId}
                  skinColor="standard"
                  inSolutionPage={true}
                  isBackToReferrerMode={showBackToReferrerButton}
                />
              )}
              <Box display={isMobile ? 'none' : 'initial'}>
                <SolutionBreadcrumbsSection
                  {...solutionBreadcrumbsSectionProps}
                />
              </Box>
            </Box>
          </Cell>
          <SolutionHelper
            solutionId={solutionId}
            appName={solutionInfoProps.solutionDetailsProps.name}
            appDependencies={appDependencies}
            isNotAvailableToInstall={
              solutionInfoProps.solutionDetailsProps.isNotAvailableToInstall
            }
            preventNewOrdersInstallation={
              solutionInfoProps.solutionDetailsProps
                .preventNewOrdersInstallation
            }
          />
          <Cell span={12}>
            <SolutionInfo
              {...solutionInfoProps}
              appDependencies={appDependencies}
            />
            <div className={s.infoDivider}>
              <Divider />
            </div>
          </Cell>
          <Cell span={12}>
            <VisibilityDetector
              onVisible={() => onSectionImpression('SolutionOverview', 1)}
            >
              <SolutionOverView {...solutionOverViewProps} />
              <Divider />
            </VisibilityDetector>
          </Cell>
          {!shouldSwitchOrderPricingAndReviews ? (
            <>
              <Cell>
                <VisibilityDetector
                  onVisible={() => onSectionImpression('SolutionReviews', 2)}
                >
                  <ReviewsPreivewWrapper>
                    <SolutionRateReviewsContainer
                      appName={solutionInfoProps.solutionDetailsProps.name}
                      developerName={developerName}
                      solutionId={solutionId}
                      developerIcon={solutionDeveloperIcon}
                      showEmptyState={mode === AppPageMode.TEST}
                      isMobile={isMobile}
                      isWixOffering={
                        solutionInfoProps.solutionDetailsProps
                          .showRegularAppPage
                      }
                    />
                  </ReviewsPreivewWrapper>
                </VisibilityDetector>
              </Cell>
              <Cell>
                <VisibilityDetector
                  onVisible={() =>
                    onSectionImpression('SolutionPricingPlans', 3)
                  }
                >
                  <PlansPreivewWrapper {...solutionPlansProps}>
                    <SolutionPlans {...solutionPlansProps} />
                  </PlansPreivewWrapper>
                </VisibilityDetector>
              </Cell>
            </>
          ) : (
            <>
              <Cell>
                <VisibilityDetector
                  onVisible={() =>
                    onSectionImpression('SolutionPricingPlans', 3)
                  }
                >
                  <PlansPreivewWrapper {...solutionPlansProps}>
                    <SolutionPlans {...solutionPlansProps} />
                  </PlansPreivewWrapper>
                </VisibilityDetector>
              </Cell>
              <Cell>
                <VisibilityDetector
                  onVisible={() => onSectionImpression('SolutionReviews', 2)}
                >
                  <ReviewsPreivewWrapper>
                    <SolutionRateReviewsContainer
                      appName={solutionInfoProps.solutionDetailsProps.name}
                      developerName={developerName}
                      solutionId={solutionId}
                      developerIcon={solutionDeveloperIcon}
                      showEmptyState={mode === AppPageMode.TEST}
                      isMobile={isMobile}
                      isWixOffering={
                        solutionInfoProps.solutionDetailsProps
                          .showRegularAppPage
                      }
                    />
                  </ReviewsPreivewWrapper>
                </VisibilityDetector>
              </Cell>
            </>
          )}
          {mode !== AppPageMode.TEST && (
            <Cell>
              <VisibilityDetector
                onVisible={() => onSectionImpression('SolutionRelatedApps', 4)}
              >
                <div className={s.suggestionDivider}>
                  <Divider />
                </div>
                <SuggestionPreviewWrapper>
                  <SolutionsGridContainer
                    {...solutionsGridProps}
                    locale={locale}
                    countryCode={countryCode}
                    getAppInstance={getAppInstance}
                  />
                </SuggestionPreviewWrapper>
              </VisibilityDetector>
            </Cell>
          )}
        </FluidContainer>
      );
    },
  );
